import { Card } from "@components/common/card";
import { Separator } from "@components/common/separator";
import { Badge } from "@components/common/badge";
import { cn } from "@pma/utils/helpers";
import { DictType } from "@pma/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@stores/auth-store";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import axios from "axios";
import {
  SERVER_URL,
  USER_ROLE_DICTIONARY_ENDPOINT,
} from "@pma/utils/api-routes";
import PmsLoader from "@components/common/loader";
import { Button } from "@components/common/button";
import { PlusCircle, X } from "lucide-react";
import { toast } from "@hooks/use-toast";
import useTranslator from "@hooks/use-translator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/common/popover";
import RolePermissions from "./role-permissions";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/common/tooltip";
import CreateRole from "./add-role";

const UserRolesSettings = () => {
  const accessToken = parseCookies()[ACCESS_TOKEN];

  const { authConfig } = useAuth();
  const { translate } = useTranslator();

  const orgId = authConfig?.last_organization_id;

  const getRoles = async () => {
    const response = await axios.get(
      SERVER_URL +
        `${USER_ROLE_DICTIONARY_ENDPOINT}?context_type=organization%20user&context_id=${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["roles", orgId],
    queryFn: getRoles,
  });

  const deleteRole = async (id: number) => {
    const response = await axios.delete(
      SERVER_URL + USER_ROLE_DICTIONARY_ENDPOINT + `/delete/${id}`,
      {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response?.data;
  };
  const deleteRoleMutation = useMutation({
    mutationKey: ["delete-status"],
    mutationFn: (id: number) => deleteRole(id),
    onSuccess: () => {
      refetch();
      toast({
        title: translate("Success"),
        description: translate("Roles updated"),
        variant: "success",
      });
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to delete role"),
        variant: "error",
      });
    },
  });

  return (
    <Card className="p-3 min-h-[200px]">
      <div className="flex justify-between items-center">
        <h3 className="tex-lg font-semibold">{translate("Member Roles")}</h3>
        <CreateRole refetch={refetch} />
      </div>

      <Separator className="my-2" />

      {isLoading && <PmsLoader />}

      <div className="flex gap-3 flex-wrap mt-4">
        {data?.map((role: DictType, idx: number) => (
          <RolePermission key={role?.id} role={role}>
            <Tooltip>
              <TooltipTrigger>
                <Badge
                  className={cn(
                    "relative text-black capitalize cursor-pointer",
                    "bg-gradient-to-br",
                    idx % 2 === 0
                      ? "from-primary to-muted"
                      : "from-muted to-primary",
                    "bg-chart-2",
                  )}
                >
                  {role?.name}
                  <Button
                    variant={"outline"}
                    size={"icon"}
                    className="absolute -top-1 -right-2 h-3 w-3 text-red-500 hover:scale-125 rounded-full"
                    onClick={() => deleteRoleMutation.mutate(role?.id)}
                  >
                    <X className="rounded-md" />
                  </Button>
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="bg-white text-black">
                {translate("View permissions")}
              </TooltipContent>
            </Tooltip>
          </RolePermission>
        ))}
      </div>
    </Card>
  );
};

export default UserRolesSettings;

const RolePermission = ({ children, role }: any) => {
  return (
    <Popover>
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent className="border-none bg-transparent shadow-none w-fit">
        <RolePermissions role={role} />
      </PopoverContent>
    </Popover>
  );
};
